import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collectionsApi, CollectionCreateInput, CollectionUpdateInput } from '../api/collections';
import { useToast } from '../components/ui/Toast';
import { isDuplicateError } from '../utils/errors';

// Query keys
export const collectionKeys = {
  all: ['collections'] as const,
  lists: () => [...collectionKeys.all, 'list'] as const,
  list: () => [...collectionKeys.lists()] as const,
  details: () => [...collectionKeys.all, 'detail'] as const,
  detail: (id: string) => [...collectionKeys.details(), id] as const,
  recipes: (id: string) => [...collectionKeys.detail(id), 'recipes'] as const,
};

export function useCollections() {
  return useQuery({
    queryKey: collectionKeys.list(),
    queryFn: () => collectionsApi.list(),
  });
}

export function useCollection(id: string) {
  return useQuery({
    queryKey: collectionKeys.detail(id),
    queryFn: () => collectionsApi.get(id),
    enabled: !!id,
  });
}

export function useCollectionRecipes(id: string) {
  return useQuery({
    queryKey: collectionKeys.recipes(id),
    queryFn: () => collectionsApi.getRecipes(id),
    enabled: !!id,
  });
}

export function useCreateCollection() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (data: CollectionCreateInput) => collectionsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
      showToast('Collection created', 'success');
    },
    onError: (error: Error) => {
      if (isDuplicateError(error)) {
        showToast('A collection with this name already exists', 'error');
      } else {
        showToast(error.message || 'Failed to create collection', 'error');
      }
    },
  });
}

export function useUpdateCollection() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CollectionUpdateInput }) => collectionsApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
      showToast('Collection updated', 'success');
    },
    onError: (error: Error) => {
      if (isDuplicateError(error)) {
        showToast('A collection with this name already exists', 'error');
      } else {
        showToast(error.message || 'Failed to update collection', 'error');
      }
    },
  });
}

export function useDeleteCollection() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => collectionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
      showToast('Collection deleted', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete collection', 'error');
    },
  });
}

export function useAddRecipeToCollection() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ collectionId, recipeId }: { collectionId: string; recipeId: string }) =>
      collectionsApi.addRecipe(collectionId, recipeId),
    onSuccess: (_, { collectionId }) => {
      queryClient.invalidateQueries({
        queryKey: collectionKeys.recipes(collectionId),
      });
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
      showToast('Recipe added to collection', 'success');
    },
    onError: (error: Error) => {
      if (isDuplicateError(error)) {
        showToast('Recipe is already in this collection', 'info');
      } else {
        showToast(error.message || 'Failed to add recipe to collection', 'error');
      }
    },
  });
}

export function useRemoveRecipeFromCollection() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ collectionId, recipeId }: { collectionId: string; recipeId: string }) =>
      collectionsApi.removeRecipe(collectionId, recipeId),
    onSuccess: (_, { collectionId }) => {
      queryClient.invalidateQueries({
        queryKey: collectionKeys.recipes(collectionId),
      });
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
      showToast('Recipe removed from collection', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to remove recipe from collection', 'error');
    },
  });
}
